import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Box, Button, FormControl, FormControlLabel, Input, MenuItem, RadioGroup, Radio, Select, Checkbox, Tooltip } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import PopupCommon from "@components/popup/popup_common";

import { useAuthStore } from "@user/store/auth_store";

import SearchPnPopup from "@user/views/popup/popup_search_pn";

import searchIcon from "@user/assets/images/common/search.svg";

interface State {
  editMode: string,
  isShowSearchPn: boolean,
  isHoverBtn: boolean
}

const defState = {
  editMode: "add",
  isShowSearchPn: false,
  isHoverBtn: false,
};

interface Info {
  skuStockNo: string,
  mngType: string,
  wh: string,
  pnNo: string,
  pn: string,
  trSn: string,
  trNoInfos: Code[],
  trNo: string,
  trNoNewYn: string,
  snInfos: Code[],
  sn: string,
  snNewYn: string,
  kwd: string,
  ctg: string,
  uom: string,
  locationId: string,
  location1: string,
  location2: string,
  location3: string,
  location4: string,
  stockQty: string,
  remark: string
}

const defInfo = {
  skuStockNo: "",
  mngType: "E",
  wh: "",
  pnNo: "",
  pn: "",
  trSn: "",
  trNoInfos: [],
  trNo: "",
  trNoNewYn: "N",
  snInfos: [],
  sn: "",
  snNewYn: "N",
  kwd: "",
  ctg: "",
  uom: "",
  locationId: "",
  location1: "",
  location2: "",
  location3: "",
  location4: "",
  stockQty: "",
  remark: ""
};

const validationMsg = [
  {id: "mngType", type: "NotBlank", msg: "관리구분을 선택해 주세요."},
  {id: "wh", type: "NotBlank", msg: "창고를 선택해 주세요."},
  {id: "pnNo", type: "NotBlank", msg: "P/N을 선택해 주세요."},
  {id: "pn", type: "NotBlank", msg: "P/N을 선택해 주세요."},
  {id: "trSn", type: "NotBlank", msg: "TR No, S/N를 선택해 주세요."},
  {id: "location1", type: "NotBlank", msg: "Location을 선택해 주세요."},
  {id: "location2", type: "NotBlank", msg: "Location을 선택해 주세요."},
  {id: "location3", type: "NotBlank", msg: "Location을 선택해 주세요."},
  {id: "location4", type: "NotBlank", msg: "Location을 선택해 주세요."},
  {id: "stockQty", type: "NotNull", msg: "Stock Qty를 입력해 주세요."},
  {id: "stockQty", type: "Min", msg: "Stock Qty를 확인해 주세요."},
  {id: "stockQty", type: "Max", msg: "Stock Qty를 확인해 주세요."},
  {id: "skuNo", type: "NotFound", msg: "Sku 정보가 없습니다."},
  {id: "skuCellQty", type: "InvalidStatus", msg: "적재 가능 SKU Cell이 없습니다."},
  {id: "stockRate", type: "InvalidStatus", msg: "적재 가능 SKU Cell을 초과하였습니다."},
  {id: "pn", type: "InvalidStatus", msg: "다른 창고의 동일한 P/N은 입고할 수 없습니다."}
];

const validationMsg4searchPn = [
  {id: "pn", type: "NotFound", msg: "PN 정보가 없습니다."}
];

function PopupStockAdd(props: any) {
  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
  }

  async function searchInfo() {
  }

  async function closePopup() {
    props.onClose();
  }

  async function checkPn() {
    if (!info.wh) {
      kutil.alert("창고를 선택해 주세요.");
      return;
    }

    if (info.pn) {
      searchPn();
    } else {
      openPopupSearchPn()
    }
  }

  async function searchPn(pn: string = info.pn) {
    try {
        const data = await kutil.sendGet("/api/stock/stock_status.data?mode=searchPn", {mngType: info.mngType, wh: info.wh, pn: pn}, {
          validationMsg: validationMsg4searchPn
        });
        (data.trNoInfos as any[]).forEach((item) => {
          item.code = item.trNo;
          item.value = item.trNo;
        });
        (data.snInfos as any[]).forEach((item) => {
          item.code = item.sn;
          item.value = item.sn;
        });
        setInfo(prev => ({ ...prev, pnNo: data.pnInfo.pnNo, pn: pn, trNoInfos: data.trNoInfos, snInfos: data.snInfos, kwd: data.pnInfo.kwd, ctg: data.pnInfo.ctg, uom: data.pnInfo.uom, remark: data.pnInfo.remark }));
    } catch(e) {
      console.log(e);
    }
  }

  function changeLocation(e: any) {
    const name = e.target.name as string;
    const value = e.target.value as string;

    const next: any = { ...info, [name]: value };

    if (name === "location1" && value !== "") {
      const isHigh = Number(value) >= 6;
      if (Number(next.location3) > (isHigh ? 18 : 45)) next.location3 = "";
      if (Number(next.location4) > (isHigh ? 15 : 76)) next.location4 = "";
    }

    const allFilled =
      next.location1 && next.location2 && next.location3 && next.location4;

    next.locationId = allFilled
      ? valueFormatter.locationIdParser(
          next.location1, next.location2, next.location3, next.location4, "0"
        )
      : "";

    setInfo(next);
  }

  async function save() {
    info.trSn = (info.trNo || "") + (info.sn || "");
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    if (await kutil.confirm("등록하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/stock_status.proc?mode=addSkuStock", info, {
        validationMsg: validationMsg
      });
      await kutil.alert("등록하였습니다.");
      props.onClose({isSuccess: true});
    } catch(e) {
      console.log(e);
    }
  }

  async function openPopupSearchPn() {
    setState(prev => ({ ...prev, isShowSearchPn: true }));
  }

  async function closePopupSearchPn(data: any) {
    setState(prev => ({ ...prev, isShowSearchPn: false }));

    if (data?.isSuccess) {
      searchPn(data.pn);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "900px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          재고 추가
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 재고 정보 */}
            <div className="table-wrap">
              <table>
                <colgroup>
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "33%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "33%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th scope="row">관리구분</th>
                    <td>
                      <div className="form-base">
                        <FormControl>
                          <RadioGroup
                            row
                            value={info.mngType}
                            onChange={(e) => {setInfo(prev => ({ ...prev, mngType: e.target.value, wh: "", pn: "", pnNo: "", trNoInfos: [], trNo: "", trNoNewYn: "N", snInfos: [], sn: "", snNewYn: "N", kwd: "", ctg: "", uom: "", remark: "" })) }}
                          >
                          {(commonCode.mngType as any[]).map((code) => (
                            <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                          ))}
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </td>
                    <th scope="row">창고<em>*</em></th>
                    <td>
                      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                        <Select
                          value={info.wh}
                          onChange={(e) => {setInfo(prev => ({ ...prev, wh: e.target.value }))}}
                          displayEmpty
                        >
                          <MenuItem value="">선택</MenuItem>
                          {(commonCode.wh as any[]).filter(c => info.mngType === "B" ? c.code === "B" : c.code !== "B").map(code => (
                            <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">P/N<em>*</em></th>
                    <td>
                      <div className={state.isHoverBtn ? "input-btn on" : "input-btn"}>
                        <Input
                          inputProps={{ maxLength: 100 }}
                          value={info.pn}
                          onChange={(e) => {setInfo(prev => ({ ...prev, pn: e.target.value, pnNo: "", trNoInfos: [], trNo: "", snInfos: [], sn: "", kwd: "", ctg: "", uom: "", remark: "" })) }}
                          onKeyDown={(e) => { kutil.onKeyDownForInput(e, checkPn) }}
                        />
                        <button
                          onMouseEnter={() => setState(prev => ({ ...prev, isHoverBtn: true }))}
                          onMouseLeave={() => setState(prev => ({ ...prev, isHoverBtn: false }))}
                          onClick={checkPn}
                        >
                          <Tooltip title="검색">
                            <img src={searchIcon} alt="검색" />
                          </Tooltip>
                        </button>
                      </div>
                    </td>
                    <th scope="row">TR No</th>
                    <td>
                      <div className="form-div">
                      {info.trNoNewYn == "N" ?
                        (
                        <FormControl>
                          <Select
                            value={info.trNo}
                            onChange={(e) => {setInfo(prev => ({ ...prev, trNo: e.target.value, sn: "" }))}}
                            displayEmpty
                          >
                            <MenuItem value="">선택</MenuItem>
                            {(info.trNoInfos as any[]).map((code) => (
                              <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        ) : (
                        <Input
                          inputProps={{ maxLength: 100 }}
                          value={info.trNo}
                          onChange={(e) => {setInfo(prev => ({ ...prev, trNo: e.target.value, sn: "" }))}}
                          onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                        />
                        )
                      }
                        <FormControlLabel control={<Checkbox size="small" />} label="신규" onChange={(e) => {setInfo(prev => ({ ...prev, trNoNewYn: ((e.target as HTMLInputElement).checked ? "Y" : "N") }))}} />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">S/N</th>
                    <td>
                      <div className="form-div">
                      {info.snNewYn == "N" ?
                        (
                        <FormControl>
                          <Select
                            value={info.sn}
                            onChange={(e) => {setInfo(prev => ({ ...prev, sn: e.target.value, trNo: "" }))}}
                            displayEmpty
                          >
                            <MenuItem value="">선택</MenuItem>
                            {(info.snInfos as any[]).map((code) => (
                              <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        ) : (
                        <Input
                          inputProps={{ maxLength: 100 }}
                          value={info.sn}
                          onChange={(e) => {setInfo(prev => ({ ...prev, sn: e.target.value, trNo: "" }))}}
                          onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                        />
                        )
                      }
                        <FormControlLabel control={<Checkbox size="small" />} label="신규" onChange={(e) => {setInfo(prev => ({ ...prev, snNewYn: ((e.target as HTMLInputElement).checked ? "Y" : "N") }))}} />
                      </div>
                    </td>
                    <th scope="row">KWD</th>
                    <td>
                      { info.kwd }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">CTG</th>
                    <td>
                      { info.ctg }
                    </td>
                    <th scope="row">UOM</th>
                    <td>
                      { info.uom }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Location<em>*</em></th>
                    <td>
                      <div className="form-div unit04">
                        <FormControl>
                          <Select
                            name="location1"
                            value={info.location1}
                            onChange={changeLocation}
                            displayEmpty
                          >
                            <MenuItem value="">SC</MenuItem>
                            {Array.from({ length: kutil.LOCATION_RANGE.level1 }, (_, i) => {
                              const v = String(i + 1);
                              return (
                                <MenuItem key={v} value={v}>
                                  {v}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <Select
                            name="location2"
                            value={info.location2}
                            onChange={changeLocation}
                            displayEmpty
                          >
                            <MenuItem value="">열</MenuItem>
                            {Array.from({ length: kutil.LOCATION_RANGE.level2 }, (_, i) => {
                              const v = String(i + 1);
                              return (
                                <MenuItem key={v} value={v}>
                                  {v}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <Select
                            name="location3"
                            value={info.location3}
                            onChange={changeLocation}
                            displayEmpty
                          >
                            <MenuItem value="">행</MenuItem>
                            {Array.from({ length: (Number(info.location1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level3.wp : kutil.LOCATION_RANGE.level3.wb) }, (_, i) => {
                              const v = String(i + 1);
                              return (
                                <MenuItem key={v} value={v}>
                                  {v}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <Select
                            name="location4"
                            value={info.location4}
                            onChange={changeLocation}
                            displayEmpty
                          >
                            <MenuItem value="">단</MenuItem>
                            {Array.from({ length: (Number(info.location1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level4.wp : kutil.LOCATION_RANGE.level4.wb) }, (_, i) => {
                              const v = String(i + 1);
                              return (
                                <MenuItem key={v} value={v}>
                                  {v}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </div>
                    </td>
                    <th scope="row">Stock Qty<em>*</em></th>
                    <td>
                      <Input
                        inputProps={{ maxLength: 7 }}
                        value={info.stockQty}
                        onChange={(e) => {setInfo(prev => ({ ...prev, stockQty: valueFormatter.number(valueFormatter.numberParser(e.target.value)) }))}}
                        onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th scope="col" colSpan={4} className="ta-center">Remark</th>
                  </tr>
                  <tr>
                    <td colSpan={4}>
                      { info.remark }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*// 재고 정보 */}
            {state.isShowSearchPn &&
              <PopupCommon onClose={closePopupSearchPn}>
                <SearchPnPopup id={props.id} data={{mngType: info.mngType}} />
              </PopupCommon>
            }
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            취소
          </Button>
          { auth.func1 &&
          <Button variant="contained" autoFocus onClick={save}>
            저장
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupStockAdd);
